import * as THREE from 'three'
import NidoPart, {
  NidoPartParameters,
} from '~/service/experience/world/nido/parts/NidoPart'
import { Nido, NidoSingle } from '~/service/experience/world/nido/Nido'
import {
  NidoCategory,
  NidoId,
  NidoCode,
  NidoPosition,
  NidoPartName,
  NidoSocketCode,
  NidoSocketId,
  NidoSocketType,
} from '~/service/experience/world/nido/enum'
import Resources from '~/service/experience/utils/Resources'
import NidoTable from '~/service/experience/world/nido/parts/NidoTable'
import NidoSocket from '~/service/experience/world/nido/parts/NidoSocket'

export class NidoTexturePart extends NidoPart {
  textureId: number
  parent: Nido
  // TODO
  category: NidoCategory

  constructor(
    parent: Nido,
    scene: THREE.Group,
    model: THREE.Mesh,
    textureId: number,
    parameters: NidoPartParameters,
    code: string,
    id: NidoId
  ) {
    ;(model.material as THREE.Material).name = textureId.toString()

    super(parent, scene, model, parameters, code, id)

    this.parent = parent
    this.textureId = textureId

    this.setColor(textureId)
  }

  setColor(textureId: number) {
    // TODO: Add global loading screen and set new material only when it's loaded
    ;(this.model.material as THREE.Material).name = textureId.toString()

    this.textureId = textureId

    const configurator = useConfigurator()

    configurator
      .getResources()
      .getTexture(textureId, (texture: THREE.Texture) => {
        if (textureId !== this.textureId) return

        this.model.material = new THREE.MeshStandardMaterial({
          map: texture.clone(),
        })

        return this
      })

    return this
  }

  addCollider() {
    this.parent.addCollider()
  }

  toJSON() {
    const json = super.toJSON()

    const obj = JSON.parse(json)

    obj.textureId = this.textureId

    return JSON.stringify(obj, null, '\t')
  }
}

export class NidoHighBackrest extends NidoTexturePart {
  isCorner: boolean
  isInner: boolean

  constructor(
    parent: Nido,
    scene: THREE.Group,
    resources: Resources,
    textureId: number,
    offset: number,
    inverted: boolean,
    isCorner: boolean,
    isInner: boolean,
    position: NidoPosition
  ) {
    if (isInner) {
      super(
        parent,
        scene,
        resources.models.highBackrestInnerCorner,
        textureId,
        { offset, inverted, position },
        NidoCode.HIGH_BACKREST_INNER_CORNER,
        NidoId.HIGH_BACKREST_INNER_CORNER
      )
    } else if (isCorner) {
      if (position === NidoPosition.LEFT) {
        super(
          parent,
          scene,
          resources.models.highBackrestCorner,
          textureId,
          { offset, inverted, position },
          NidoCode.HIGH_BACKREST_CORNER,
          NidoId.HIGH_BACKREST_CORNER
        )
      } else if (position === NidoPosition.CENTER) {
        super(
          parent,
          scene,
          parent instanceof NidoSingle
            ? parent.neighborLeft || parent.neighborRight
              ? resources.models.highBackrestCorner
              : resources.models.highBackrestWide
            : resources.models.highBackrestCorner,
          textureId,
          { offset, inverted, position },
          NidoCode.HIGH_BACKREST_CORNER,
          NidoId.HIGH_BACKREST_CORNER
        )
        if (
          parent instanceof NidoSingle &&
          parent.neighborLeft &&
          !parent.neighborRight
        ) {
          this.model.scale.x *= -1
        }
      } else {
        super(
          parent,
          scene,
          resources.models.highBackrestCorner,
          textureId,
          { offset, inverted, position },
          NidoCode.HIGH_BACKREST_CORNER,
          NidoId.HIGH_BACKREST_CORNER
        )
      }
    } else {
      super(
        parent,
        scene,
        resources.models.highBackrest,
        textureId,
        { offset, inverted, position },
        NidoCode.HIGH_BACKREST,
        NidoId.HIGH_BACKREST
      )
    }

    if (inverted) {
      this.model.scale.z *= -1
    }

    this.isCorner = isCorner

    if (position === NidoPosition.RIGHT) {
      this.model.scale.x *= -1
    }

    this.addCollider()
  }

  toJSON() {
    const json = super.toJSON()

    const obj = JSON.parse(json)

    return JSON.stringify(obj, null, '\t')
  }
}

export class NidoPillow extends NidoTexturePart {
  constructor(
    parent: Nido,
    scene: THREE.Group,
    resources: Resources,
    textureId: number,
    parameters: NidoPartParameters,
    isCorner: boolean,
    isInner: boolean
  ) {
    const model = isCorner
      ? parent instanceof NidoSingle
        ? resources.models.pillowWide
        : isInner
        ? resources.models.pillowCornerInner
        : resources.models.pillowCornerOuter
      : resources.models.pillow

    super(
      parent,
      scene,
      model,
      textureId,
      parameters,
      parent instanceof NidoSingle
        ? NidoCode.PILLOW
        : isCorner
        ? isInner
          ? NidoCode.PILLOW
          : NidoCode.PILLOW_CORNER
        : NidoCode.PILLOW,

      parent instanceof NidoSingle
        ? NidoId.PILLOW
        : isCorner
        ? isInner
          ? NidoId.PILLOW
          : NidoId.PILLOW_CORNER
        : NidoId.PILLOW
    )

    if (parameters.inverted) {
      this.model.scale.z *= -1
    }

    this.addCollider()
  }

  toJSON() {
    const json = super.toJSON()

    const obj = JSON.parse(json)

    return JSON.stringify(obj, null, '\t')
  }
}

export class NidoLowBackrest extends NidoTexturePart {
  scene: THREE.Group
  resources: Resources
  isCorner: boolean
  isInner: boolean
  highBackrest: NidoHighBackrest | undefined
  pillow: NidoPillow | undefined

  constructor(
    parent: Nido,
    scene: THREE.Group,
    resources: Resources,
    textureId: number,
    offset: number,
    inverted: boolean,
    isCorner: boolean,
    isInner: boolean,
    position: NidoPosition,
    lowBackrest?: any
  ) {
    if (isInner) {
      super(
        parent,
        scene,
        resources.models.lowBackrestInnerCorner,
        textureId,
        { offset, inverted, position },
        NidoCode.LOW_BACKREST_INNER_CORNER,
        NidoId.LOW_BACKREST_INNER_CORNER
      )
    } else if (isCorner) {
      if (position === NidoPosition.LEFT) {
        super(
          parent,
          scene,
          resources.models.lowBackrestCorner,
          textureId,
          { offset, inverted, position },
          NidoCode.LOW_BACKREST_CORNER,
          NidoId.LOW_BACKREST_CORNER
        )
      } else if (position === NidoPosition.CENTER) {
        super(
          parent,
          scene,
          parent instanceof NidoSingle
            ? parent.neighborLeft || parent.neighborRight
              ? resources.models.lowBackrestCorner
              : resources.models.lowBackrestWide
            : resources.models.lowBackrestCorner,
          textureId,
          {
            offset,
            inverted,
            position,
          },
          NidoCode.LOW_BACKREST_CORNER,
          NidoId.LOW_BACKREST_CORNER
        )
        if (
          parent instanceof NidoSingle &&
          parent.neighborLeft &&
          !parent.neighborRight
        ) {
          this.model.scale.x *= -1
        }
      } else {
        super(
          parent,
          scene,
          resources.models.lowBackrestCorner,
          textureId,
          { offset, inverted, position },
          NidoCode.LOW_BACKREST_CORNER,
          NidoId.LOW_BACKREST_CORNER
        )
      }
    } else {
      super(
        parent,
        scene,
        resources.models.lowBackrest,
        textureId,
        { offset, inverted, position },
        NidoCode.LOW_BACKREST,
        NidoId.LOW_BACKREST
      )
    }

    this.isCorner = isCorner
    this.isInner = isInner
    this.scene = scene
    this.resources = resources

    if (position === NidoPosition.RIGHT) {
      this.model.scale.x *= -1
    }

    if (lowBackrest?.highBackrest) {
      this.addHighBackrest(lowBackrest.highBackrest.textureId)
    }
    if (lowBackrest?.pillow) {
      this.addPillow(lowBackrest.pillow.textureId)
    }

    if (inverted) {
      this.model.scale.z *= -1
    }

    this.addCollider()
  }

  addPillow(textureId: number = this.textureId) {
    this.pillow = new NidoPillow(
      this.parent,
      this.scene,
      this.resources,
      textureId,
      { offset: this.offset, inverted: this.inverted, position: this.position },
      this.isCorner && this.position === NidoPosition.CENTER,
      this.isInner
    )

    const configurator = useConfigurator()
    configurator.addedChair()
  }

  removePillow() {
    if (!this.pillow) return

    this.scene.remove(this.pillow.model)
    this.pillow = undefined

    this.addCollider()

    const configurator = useConfigurator()
    configurator.addedChair()
  }

  addHighBackrest(textureId: number = this.textureId) {
    this.highBackrest = new NidoHighBackrest(
      this.parent,
      this.scene,
      this.resources,
      textureId,
      this.offset,
      this.inverted,
      this.isCorner,
      this.isInner,
      this.position
    )

    const configurator = useConfigurator()
    configurator.addedChair()
  }

  remove() {
    this.scene.remove(this.model)
    this.removeHighBackrest()
    this.removePillow()
  }

  removeHighBackrest() {
    if (!this.highBackrest) return

    this.scene.remove(this.highBackrest.model)
    this.highBackrest = undefined

    const configurator = useConfigurator()
    configurator.addedChair()

    this.addCollider()
  }

  invert() {
    super.invert()

    this.highBackrest?.invert()
    this.pillow?.invert()
  }

  getParts(): NidoPart[] {
    const parts = [this, this.pillow, this.highBackrest]

    return parts.filter((part) => part !== undefined) as NidoPart[]
  }

  toggleCorner() {
    // TODO
  }

  toJSON() {
    const json = super.toJSON()

    const obj = JSON.parse(json)

    obj.isCorner = this.isCorner
    obj.isInner = this.isInner

    const highBackrest = this.highBackrest?.toJSON()
    const pillow = this.pillow?.toJSON()

    if (highBackrest) {
      obj.highBackrest = JSON.parse(highBackrest)
    }

    if (pillow) {
      obj.pillow = JSON.parse(pillow)
    }

    return JSON.stringify(obj, null, '\t')
  }
}

export class NidoSeat extends NidoTexturePart {
  scene: THREE.Group
  resources: Resources
  lowBackrest: NidoLowBackrest | undefined
  socket: NidoSocket | undefined

  constructor(
    parent: Nido,
    scene: THREE.Group,
    resources: Resources,
    textureId: number,
    parameters: NidoPartParameters,
    lowBackrest?: any,
    socket?: any
  ) {
    super(
      parent,
      scene,
      resources.models.seat,
      textureId,
      parameters,
      NidoCode.SEAT,
      NidoId.NONE
    )
    this.scene = scene
    this.resources = resources

    if (lowBackrest) {
      if (lowBackrest instanceof NidoLowBackrest) {
        this.lowBackrest = lowBackrest
      } else {
        this.addLowBackrest(
          lowBackrest.isCorner,
          lowBackrest.isInner,
          lowBackrest.textureId,
          lowBackrest
        )
      }
    }

    if (socket) {
      if (socket instanceof NidoSocket) {
        this.socket = socket
      } else {
        this.addSocket(socket.isCorner, socket.code, socket.id)
      }
    }

    this.addCollider()
  }

  switchToTable() {
    this.lowBackrest?.remove()
    this.scene.remove(this.model)

    this.parent.toggleAddButton(this.position || NidoPosition.CENTER, true)

    this.parent.parts[this.position] = new NidoTable(
      this.parent,
      this.scene,
      this.resources,
      { offset: this.offset, inverted: this.inverted, position: this.position },
      undefined,
      this.socket
    )
    const configurator = useConfigurator()
    configurator.addedChair()
  }

  addLowBackrest(
    isCorner: boolean,
    isInner = false,
    textureId: number = this.textureId,
    lowBackrest?: any
  ) {
    if (
      this.parent instanceof NidoSingle &&
      isCorner &&
      !this.parent.neighborLeft &&
      !this.parent.neighborRight
    ) {
      this.scene.remove(this.model)
      this.model = this.resources.models.seatWide.clone()
      this.setColor(this.textureId)
      this.scene.add(this.model)
    }

    this.lowBackrest = new NidoLowBackrest(
      this.parent,
      this.scene,
      this.resources,
      textureId,
      this.offset,
      this.inverted,
      isCorner,
      isInner,
      this.position,
      lowBackrest
    )

    const configurator = useConfigurator()
    configurator.addedChair()

    this.parent.toggleAddButton(this.position || NidoPosition.CENTER, !isCorner)
  }

  removeLowBackrest() {
    if (!this.lowBackrest) return

    if (this.parent instanceof NidoSingle) {
      this.scene.remove(this.model)
      this.model = this.resources.models.seat.clone()
      this.setColor(this.textureId)
      this.scene.add(this.model)
    }

    this.lowBackrest.remove()

    this.scene.remove(this.lowBackrest.model)
    this.lowBackrest = undefined

    const configurator = useConfigurator()
    configurator.addedChair()

    this.addCollider()

    this.parent.toggleAddButton(this.position || NidoPosition.CENTER, true)
  }

  addSocket(
    isCorner: boolean,
    socketCode?: NidoSocketCode,
    socketId?: NidoSocketId
  ) {
    if (socketCode === undefined || socketId === undefined) {
      const world = useWorld()
      const defaultSocketType =
        world.getSocketType() ?? NidoSocketType.SOCKET_CZ
      socketCode = NidoSocketCode[defaultSocketType]
      socketId = NidoSocketId[defaultSocketType]
    }
    this.socket = new NidoSocket(
      this.parent,
      this.scene,
      this.resources,
      {
        offset: this.offset,
        inverted: this.inverted,
        position: this.position,
      },
      isCorner,
      socketCode,
      socketId
    )

    const configurator = useConfigurator()
    configurator.addedChair()
  }

  removeSocket() {
    if (!this.socket) return

    this.scene.remove(this.socket.model)
    this.socket = undefined

    const configurator = useConfigurator()
    configurator.addedChair()

    this.addCollider()
  }

  invert() {
    super.invert()

    this.lowBackrest?.invert()
    this.socket?.invert()

    this.addCollider()
  }

  // TODO: Get all parts from Nido
  getParts(): NidoPart[] {
    return [this, this.lowBackrest?.getParts(), this.socket?.getParts()]
      .flat(Infinity)
      .filter((part) => part !== undefined) as NidoPart[]
  }

  toJSON() {
    const json = super.toJSON()

    const obj = JSON.parse(json)

    const lowBackrest = this.lowBackrest?.toJSON()
    const socket = this.socket?.toJSON()

    obj.type = NidoPartName.SEAT

    if (lowBackrest) {
      obj.lowBackrest = JSON.parse(lowBackrest)
    }

    if (socket) {
      obj.socket = JSON.parse(socket)
    }

    return JSON.stringify(obj, null, '\t')
  }
}
